import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { getApiError } from '@/composables/getApiError'
import { useSlidingChatGlobalBridge } from '@/composables/useChatModuleBridge'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { findScenario, resolveCondition, resolveStepText } from '../scenarios/registry'
import { waitForAnchors } from '../utils/anchors'
import { getContext, runContextAction, waitForContext } from '../utils/contextRegistry'
import { planScopeTransition } from '../utils/scopes'
import type { IOnboardingDemoItem } from '../types/IOnboardingDemoItem'
import type { IOnboardingProgressItem } from '../types/IOnboardingProgressItem'
import type { IOnboardingScenario } from '../types/IOnboardingScenario'
import type { IOnboardingSectionDemo } from '../types/IOnboardingSectionDemo'
import type { IOnboardingStep } from '../types/IOnboardingStep'
import type { IOnboardingStepText } from '../types/IOnboardingStepText'
import type { IOnboardingStoreState } from '../types/IOnboardingStoreState'
import type { TOnboardingSection } from '../types/TOnboardingSection'
import type { TOnboardingStatus } from '../types/TOnboardingStatus'

const CONTEXT_TIMEOUT_MS = 5000

/*
 * Ожидание якоря: долго — только если перед шагом открывалась форма, страница или меню
 * (им нужно время смонтироваться). Отсутствующий элемент (нет прав, нет активных фильтров)
 * пропускается, как только DOM успокоился, чтобы онбординг не подвисал.
 */
const ANCHOR_TIMEOUT_MS = 3000
const ANCHOR_TIMEOUT_STABLE_MS = 150
const ANCHOR_SETTLE_MS = 500
const AUTOSTART_DELAY_MS = 600

/** Элементы подсветки текущего шага: DOM-ссылки держим вне реактивного состояния */
let currentElements: HTMLElement[] = []

export function getCurrentElements(): HTMLElement[] {
  return currentElements
}

async function waitForGlobalLoading(timeoutMs: number): Promise<void> {
  const feedbackStore = useFeedbackStore()
  const startedAt = Date.now()

  while (feedbackStore.isGlobalLoading && Date.now() - startedAt < timeoutMs)
    await new Promise(resolve => window.setTimeout(resolve, 100))
}

/**
 * Движок онбординга.
 *
 * - Прогресс разделов: `GET /api/onboarding/progress/`, отметка — `PUT /api/onboarding/progress/{section}/`.
 * - Демо-записи: `GET /api/onboarding/sections/{section}/`, хранятся здесь и не попадают в сторы модулей.
 * - Шаги ведутся по сценарию из `scenarios/registry`: перед шагом открываются нужные ему области
 *   (форма, панель, меню) и закрываются лишние, затем ждутся контекст и якоря;
 *   не дождались или условие ложно — шаг пропускается.
 */
export const useOnboardingStore = defineStore('onboardingStore', {
  state: (): IOnboardingStoreState => ({
    progress: {},
    isProgressLoaded: false,
    demo: {},
    activeScenarioId: null,
    history: [],
    openScopes: [],
    skipped: [],
    isTransitioning: false,
    stepToken: 0,
    startedInSession: [],
  }),

  getters: {
    isActive: (state): boolean => state.activeScenarioId !== null,

    scenario: (state): IOnboardingScenario | null =>
      state.activeScenarioId ? findScenario(state.activeScenarioId) : null,

    currentIndex: (state): number => state.history.at(-1) ?? -1,

    currentStep(): IOnboardingStep | null {
      return this.scenario?.steps[this.currentIndex] ?? null
    },

    currentText(): IOnboardingStepText | null {
      return this.currentStep ? resolveStepText(this.currentStep.id) : null
    },

    /** Видимых шагов: все шаги сценария минус уже пропущенные */
    totalSteps(): number {
      return (this.scenario?.steps.length ?? 0) - this.skipped.length
    },

    position: (state): number => state.history.length,

    /** Демо-записи активного раздела, пока идёт онбординг */
    activeDemoItems(): IOnboardingDemoItem[] {
      return this.scenario ? this.demo[this.scenario.section] ?? [] : []
    },
  },

  actions: {
    async fetchProgress() {
      try {
        const { data } = await axios.get<{ sections: IOnboardingProgressItem[] }>(`${BASE_URL}/api/onboarding/progress/`)

        this.progress = Object.fromEntries(data.sections.map(item => [item.code, item.status]))
        this.isProgressLoaded = true
      }
      catch (error) {
        console.error('[onboarding] Не удалось загрузить прогресс:', error)
      }
    },

    async markProgress(section: TOnboardingSection, status: Exclude<TOnboardingStatus, null>) {
      try {
        await axios.put(`${BASE_URL}/api/onboarding/progress/${section}/`, { status })
        this.progress[section] = status
      }
      catch (error) {
        useFeedbackStore().showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось сохранить прохождение обучения'),
        })
      }
    },

    async fetchDemo(section: TOnboardingSection): Promise<boolean> {
      if (this.demo[section])
        return true

      try {
        const { data } = await axios.get<IOnboardingSectionDemo>(`${BASE_URL}/api/onboarding/sections/${section}/`)

        this.demo[section] = data.items

        return true
      }
      catch (error) {
        useFeedbackStore().showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить обучение'),
        })

        return false
      }
    },

    /** Демо-запись раздела по фиктивному id — по нему формы понимают, что открыты в демо-режиме */
    findDemoItem(section: TOnboardingSection, id: unknown): IOnboardingDemoItem | null {
      if (id === null || id === undefined)
        return null

      return this.demo[section]?.find(item => String(item.detail.id) === String(id)) ?? null
    },

    /**
     * Автостарт главного сценария модуля: только если раздел не отмечен на беке
     * и сценарий ещё не запускался в этой сессии.
     */
    async autoStart(scenarioId: string) {
      const scenario = findScenario(scenarioId)
      if (!scenario?.isMain || this.isActive || this.startedInSession.includes(scenarioId))
        return

      if (!this.isProgressLoaded)
        await this.fetchProgress()

      if (!this.isProgressLoaded || this.progress[scenario.section] !== null)
        return

      await new Promise(resolve => window.setTimeout(resolve, AUTOSTART_DELAY_MS))
      await this.start(scenarioId)
    },

    async start(scenarioId: string) {
      const scenario = findScenario(scenarioId)
      if (!scenario || this.isActive || this.isTransitioning)
        return

      this.isTransitioning = true
      this.startedInSession.push(scenarioId)
      if (!this.isProgressLoaded)
        await this.fetchProgress()

      if (!(await this.fetchDemo(scenario.section))) {
        this.isTransitioning = false

        return
      }

      // Плавающий чат лежит выше слоя онбординга и перекрыл бы подсветку
      useSlidingChatGlobalBridge().closeSlidingChat()

      this.activeScenarioId = scenarioId
      this.history = []
      this.openScopes = []
      this.skipped = []

      const shown = await this.showFrom(0)
      if (!shown)
        await this.finish(false)
    },

    async next() {
      if (!this.isActive || this.isTransitioning)
        return

      const shown = await this.showFrom(this.currentIndex + 1)
      if (!shown)
        await this.finish(true)
    },

    async prev() {
      if (this.isTransitioning || this.history.length < 2)
        return

      this.isTransitioning = true
      try {
        this.history.pop()

        const step = this.scenario!.steps[this.history.at(-1)!]
        const isScreenChanged = await this.syncScopes(step.scopes ?? [])

        let elements = await this.resolveElements(step, isScreenChanged)

        // Форму или панель могли закрыть мимо онбординга — открываем области шага заново и пробуем ещё раз
        if (!elements) {
          this.openScopes = []
          await this.syncScopes(step.scopes ?? [])
          elements = await this.resolveElements(step, true)
        }

        currentElements = elements ?? []
        this.stepToken += 1
      }
      finally {
        this.isTransitioning = false
      }
    },

    /** Крестик: при неотмеченном разделе ставит `skipped` */
    async close() {
      await this.finish(false)
    },

    /**
     * Ищет и показывает первый доступный шаг начиная с индекса.
     * Возвращает false, если до конца сценария показать нечего.
     */
    async showFrom(startIndex: number): Promise<boolean> {
      const steps = this.scenario?.steps ?? []

      this.isTransitioning = true
      try {
        for (let index = startIndex; index < steps.length; index += 1) {
          const step = steps[index]

          // Шаг считается в пропущенных один раз: после «Назад» и повторного «Далее» его проверяют снова
          if (step.condition && !resolveCondition(step.condition)) {
            if (!this.skipped.includes(index))
              this.skipped.push(index)
            continue
          }

          const isScreenChanged = await this.syncScopes(step.scopes ?? [])

          const elements = await this.resolveElements(step, isScreenChanged)
          if (!elements) {
            if (!this.skipped.includes(index))
              this.skipped.push(index)
            continue
          }

          this.skipped = this.skipped.filter(skippedIndex => skippedIndex !== index)

          this.history.push(index)
          currentElements = elements
          this.stepToken += 1

          return true
        }

        return false
      }
      finally {
        this.isTransitioning = false
      }
    },

    async resolveElements(step: IOnboardingStep, isScreenChanged: boolean): Promise<HTMLElement[] | null> {
      const context = await waitForContext(step.context, CONTEXT_TIMEOUT_MS)
      if (!context)
        return null

      // Глобальный лоадер перекрывает страницу — показываем шаг, когда данные догрузились
      await waitForGlobalLoading(CONTEXT_TIMEOUT_MS)

      if (!step.anchors.length)
        return []

      const timeout = isScreenChanged || !getContext(step.context) ? ANCHOR_TIMEOUT_MS : ANCHOR_TIMEOUT_STABLE_MS

      return waitForAnchors(step.anchors, () => getContext(step.context)?.root() ?? null, timeout, ANCHOR_SETTLE_MS)
    },

    /**
     * Приводит открытые области к набору шага: лишние закрывает, недостающие открывает.
     * Возвращает true, если экран менялся (что-то открывалось или закрывалось).
     */
    async syncScopes(target: string[]): Promise<boolean> {
      const scopes = this.scenario?.scopes ?? {}
      const plan = planScopeTransition(this.openScopes, target)

      for (const name of plan.close) {
        const close = scopes[name]?.close
        if (close)
          await runContextAction(close.context, close.action)
        this.openScopes = this.openScopes.filter(scope => scope !== name)
      }

      for (const name of plan.open) {
        const open = scopes[name]?.open
        if (open && !(await waitForContext(open.context, CONTEXT_TIMEOUT_MS)))
          break

        if (open)
          await runContextAction(open.context, open.action)
        this.openScopes = [...this.openScopes, name]
      }

      return plan.close.length > 0 || plan.open.length > 0
    },

    /**
     * Завершение онбординга.
     * Отметку ставит только главный сценарий модуля: «Готово» — `completed`,
     * крестик — `skipped`, но только если раздел ещё не отмечен.
     */
    async finish(isCompleted: boolean) {
      const scenario = this.scenario

      await this.syncScopes([])
      currentElements = []
      this.activeScenarioId = null
      this.history = []
      this.skipped = []
      this.isTransitioning = false

      if (!scenario?.isMain || !this.isProgressLoaded)
        return

      const status = this.progress[scenario.section]
      if (isCompleted && status !== 'completed')
        await this.markProgress(scenario.section, 'completed')
      else if (!isCompleted && status === null)
        await this.markProgress(scenario.section, 'skipped')
    },
  },
})
